class HeyzineService {
  constructor(apiKey) {
    this.apiKey = apiKey;
    this.baseUrl = 'https://heyzine.com/api1/rest';
  }

  /**
   * Create a flipbook from a PDF URL
   * @param {string} pdfUrl - Direct URL to the PDF file
   * @param {Object} options - Flipbook options
   * @returns {Promise<Object>} Response with flipbook URL and metadata
   */
  async createFlipbook(pdfUrl, options = {}) {
    try {
      // Build query parameters
      const params = new URLSearchParams({
        k: this.apiKey,
        pdf: pdfUrl,
        title: options.title || 'Flipbook',
        description: options.description || '',
      });

      // Add optional parameters if provided
      if (options.page_flip_sound !== undefined) {
        params.append('page_flip_sound', options.page_flip_sound);
      }
      if (options.show_download_pdf !== undefined) {
        params.append('show_download_pdf', options.show_download_pdf);
      }
      if (options.show_thumbnails !== undefined) {
        params.append('show_thumbnails', options.show_thumbnails);
      }
      if (options.show_share_button !== undefined) {
        params.append('show_share_button', options.show_share_button);
      }
      if (options.show_toc !== undefined) {
        params.append('show_toc', options.show_toc);
      }
      if (options.show_logo !== undefined) {
        params.append('show_logo', options.show_logo);
      }

      const requestUrl = `${this.baseUrl}?${params.toString()}`;
      
      console.log('Heyzine API Request URL:', requestUrl);

      const response = await fetch(requestUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Heyzine API Error Response:', errorText);
        throw new Error(`Heyzine API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Heyzine API Response:', data);

      // Validate response structure
      if (!data.url) {
        console.error('Invalid Heyzine response:', data);
        throw new Error('Invalid response from Heyzine: No flipbook URL returned');
      }

      // Return the complete response
      return {
        url: data.url,
        thumbnail: data.thumbnail,
        pdf: data.pdf,
        id: data.id,
        meta: data.meta,
        embedCode: this.getEmbedCode(data.url),
      };

    } catch (error) {
      console.error('Heyzine Service Error:', error);
      throw error;
    }
  }

  /**
   * Generate embed code for a flipbook URL
   * @param {string} flipbookUrl - The flipbook URL from Heyzine
   * @param {number} width - Embed width (default: 800)
   * @param {number} height - Embed height (default: 600)
   * @returns {string} HTML embed code
   */
  getEmbedCode(flipbookUrl, width = 800, height = 600) {
    return `<iframe src="${flipbookUrl}" width="${width}" height="${height}" frameborder="0" allowfullscreen></iframe>`;
  }

  /**
   * Delete a flipbook
   * @param {string} documentId - The document ID returned from createFlipbook
   * @returns {Promise<Object>} Response from Heyzine
   */
  async deleteFlipbook(documentId) {
    try {
      const params = new URLSearchParams({
        k: this.apiKey,
        delete: documentId,
      });

      const requestUrl = `${this.baseUrl}?${params.toString()}`;
      
      console.log('Heyzine Delete Request:', requestUrl);

      const response = await fetch(requestUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete flipbook: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Delete Response:', data);
      
      return data;

    } catch (error) {
      console.error('Delete Flipbook Error:', error);
      throw error;
    }
  }

  /**
   * Test the API key
   * @returns {Promise<boolean>} True if API key is valid
   */
  async testApiKey() {
    try {
      // Use a test PDF URL
      const testPdfUrl = 'https://www.aeee.in/wp-content/uploads/2020/08/Sample-pdf.pdf';
      
      const result = await this.createFlipbook(testPdfUrl, {
        title: 'API Test',
        description: 'Testing API Key',
      });

      console.log('API Key Test Result:', result);

      // Clean up the test flipbook
      if (result.id) {
        await this.deleteFlipbook(result.id);
      }

      return true;
    } catch (error) {
      console.error('API Key Test Failed:', error);
      return false;
    }
  }
}

export default HeyzineService;